import dayjs from "dayjs"
import Parse from "parse"

import { getFilteredPackagingExecutions, getPackagingExecutionById, savePackagingExecutionNewStatus, savePackagingExecutionPackagingTimes } from "../../parseManager/packagingExecutions/parsePackagingExecutionManager"
import { getSiteById, getSites } from "../../parseManager/site/parseSiteManager"
import { dateToTimestamp, roundNumber } from "../../utils"
import { openAppMessageSnackBar } from "../Utils/app"
import { onEnter, push, getReplacingPath, actionWithLoader } from "../Utils/utils"
import { editableHourToTimestamp, PSE_REGEX_HOURS } from "../../utils/productionStepExecution"
import { getPackagingExecutionSiteSelector, getPackagingExecutionsSelector } from "../../reducers/Packaging/packagingExecution"
import { PE_STATUSES } from "../../utils/packagingExecutions"
import { PackagingLine } from "../Site/sites"
import { getCurrentUser } from "../../reducers/Utils/app"
import { packagingCardOrder, packagingNature } from "../../utils/packaging"
import { loadPlanningRules } from "../Misc/Misc"

const PackagingExecution = Parse.Object.extend("PackagingExecutions")

export const packagingExecutionBaseUrl = "/packagingExecutions"

export const PACKAGING_EXECUTION_URLS = {
	root: packagingExecutionBaseUrl,
	filter: `${packagingExecutionBaseUrl}/filter`,
	read: `${packagingExecutionBaseUrl}/read`,
	hoursEdit: `${packagingExecutionBaseUrl}/hours`,
	pe: `${packagingExecutionBaseUrl}/packagingExecution`
}

export const generatePackagingExecutionsVolumes = (packagingDate) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const email = Parse.User.current() ? Parse.User.current().get("email") : null
			const result = await Parse.Cloud.run("generatePackagingExecutionsVolumes", { packagingDate, email })
			if (result.success) {
				dispatch(openAppMessageSnackBar(
					"Les volumes de barquettage ont bien été générées pour la journée du " + dayjs(packagingDate).format("DD/MM/YYYY"), 
					"success"
				))
			}
			else {
				dispatch(openAppMessageSnackBar(result.message, "error"))
			}
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}


const loadPackagingExecutionsFilters = () => {
	return actionWithLoader(async (dispatch) => {
		const localStorage = window.localStorage
		const previousSelectedSite = localStorage.getItem("packagingExecutionSelectedSite")
		const date = new Date()
		const { sites } = await getSites()

		dispatch({
			type: "PACKAGING_EXECUTIONS_FILTERS_LOADED",
			date,
			sites: sites,
			site: previousSelectedSite ? JSON.parse(previousSelectedSite) : sites[0]
		})
	})
}

export const loadPackagingExecutionsRead = (params) => {
	return actionWithLoader(async (dispatch) => {
		const siteId = params.siteId
		const date = params.date
		const site = await getSiteById(siteId)
		const filters = { packagingDate: dateToTimestamp(date) }

		if (window.location.pathname.includes("hours")) {
			const status = window.localStorage.getItem("packagingExecutionStatus")
			if (status === "WEIGHT_TO_VALIDATE") {
				filters.status = ["WEIGHT_TO_VALIDATE", "LOCKED"]
			}
			if (status === "TODO") {
				filters.status = ["TODO"]
			}
		}
		const packagingExecutions = await getFilteredPackagingExecutions({ include: ["packagingLine", "productionItems"], filters })
		dispatch({
			type: "PACKAGING_EXECUTION_FILTERS_SELECTED",
			selectedSite: site,
			selectedDate: date
		})
		dispatch({
			type: "PACKAGING_EXECUTIONS_LOADED",
			packagingExecutions
		})
	})
}

export const loadPackagingExecutionProposedWeight = async (packagingExecutionId) => {
	return await Parse.Cloud.run("loadPackagingExecutionProposedWeight", { packagingExecutionId })
}

export const loadPackagingExecution = (params) => {
	return actionWithLoader(async (dispatch, getState) => {
		const selectedDate = params.date
		let selectedSite = getPackagingExecutionSiteSelector(getState())
		if (!selectedSite) { // page refresh
			const siteId = params.siteId
			selectedSite = await getSiteById(siteId)
		}
		const packagingExecution = await getPackagingExecutionById({
			id: params.packagingExecutionId,
			include: ["sections.section", "recipe", "recipe.internalTag", "packagingLine"]
		})

		let proposedWeights = null
		if (packagingExecution.status === PE_STATUSES.toValidate) {
			proposedWeights = await loadPackagingExecutionProposedWeight(packagingExecution.objectId)
		}

		dispatch({
			type: "PACKAGING_EXECUTION_FILTERS_SELECTED",
			selectedSite,
			selectedDate
		})
		dispatch({
			type: "PACKAGING_EXECUTION_LOADED",
			packagingExecution,
			packagingExecutionProposedWeights: proposedWeights
		})
	})
}


export const updatePackagingExecutionFilters = (siteId) => {
	return async () => {
		const site = await getSiteById(siteId)
		const localStorage = window.localStorage
		localStorage.setItem("packagingExecutionSelectedSite", JSON.stringify(site))
	}
}


export const updatePackagingExecution = (id, values) => {
	return actionWithLoader(async (dispatch) => {
		const packagingExecution = await new Parse.Query(PackagingExecution)
			.equalTo("objectId", id)
			.first()

		if (packagingExecution) {
			// --------- updating theoritical time --------- //
			if (PSE_REGEX_HOURS.test(values.theoreticalStartTime)) {
				const startTimeToTimeStamp = editableHourToTimestamp(values.theoreticalStartTime, packagingExecution.get("packagingDate"))
				packagingExecution.set("theoreticalStartTime", startTimeToTimeStamp)
			}

			if (PSE_REGEX_HOURS.test(values.theoreticalEndTime)) {
				const endTimeToTimeStamp = editableHourToTimestamp(values.theoreticalEndTime, packagingExecution.get("packagingDate"))
				packagingExecution.set("theoreticalEndTime", endTimeToTimeStamp)
			}

			// id to parse pointer
			if (values.packagingLine) {
				const packagingLine = await new Parse.Query(PackagingLine)
					.equalTo("objectId", values.packagingLine)
					.first()

				if (packagingLine) {
					packagingExecution.set("packagingLineName", packagingLine.get("name"))
					packagingExecution.set("packagingLine", packagingLine)
				}
			}

			const savedPackagingExecution = await packagingExecution.save()
			dispatch({
				type: "PACKAGING_EXECUTIONS_UDPATED",
				packagingExecution: savedPackagingExecution.toJSON()
			})
		}
	})
}

export const updatePackagingExecutions = (packagingExecutions) => {
	return actionWithLoader(async (dispatch) => {
		for (const packagingExecution of packagingExecutions) {
			await dispatch(updatePackagingExecution(packagingExecution.id, packagingExecution))
		}
	})
}

export const validatePackagingExecutionSectionWeights = (packagingExecutionId, changedSections = [], changedPackagings = []) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })

			// update sections
			packagingExecution.get("sections").forEach((section) => {
				const matchingChangedSection = changedSections.find((changedSection) => changedSection.section.objectId === section.section.id)
				if (matchingChangedSection) {
					section.realWeight = roundNumber(matchingChangedSection.realWeight, 3) || 0
					section.forecastWaste = roundNumber(matchingChangedSection.forecastWaste, 1) || 0
				}
			})
			// update packagings
			packagingExecution.get("packagings").forEach((packaging) => {
				const matchingChangedPackaging = changedPackagings.find((changedPackaging) => changedPackaging.packaging.objectId === packaging.packaging.id)
				if (matchingChangedPackaging) {
					packaging.forecastNumber = matchingChangedPackaging.forecastNumber || 0
				}
			})
			// todo update/add comments
			// change status and save
			await savePackagingExecutionNewStatus(packagingExecution, PE_STATUSES.todo)

			dispatch(openAppMessageSnackBar("Les poids des sections ont bien été enregistrés"))
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

/**
 * Applies all realNumber updates to the packagingExecution packagings.
 * 
 * For each updated packaging:
 * - Updates the corresponding brand's realNumber.
 * - Sets the packaging's total realNumber as the sum of its brands.
 * - Sets packaging.endTime to now.
 * - Starts the next packaging if it exists.
 * 
 * If any of the updated packagings is the last in the list,
 * the PE status is updated to WASTE. Otherwise, the PE is saved as-is.
 */
export const validateAllPackagingExecutionRealNumbers = (packagingExecutionId, packagingUpdates) => {
  return actionWithLoader(async (dispatch) => {
    try {
      const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })
      const packagings = packagingExecution.get("packagings")

      // Group updates by packagingId
      const updatesByPackaging = {}
			packagingUpdates.forEach(({ packagingId, brand, realNumber }) => {
				(updatesByPackaging[packagingId] ??= []).push({ brand, realNumber })
			})

      // Apply updates
      Object.entries(updatesByPackaging).forEach(([packagingId, brandUpdates]) => {
        const packaging = packagings.find(packaging => packaging.packaging.id === packagingId)
        if (!packaging) return

        // Update brand.realNumber inside packaging.brands
        brandUpdates.forEach(({ brand, realNumber }) => {
          const brandObj = packaging.brands.find(b => b.brand === brand)
          if (brandObj) brandObj.realNumber = realNumber
        })

        // Compute total realNumber at packaging level
        const totalReal = packaging.brands.reduce((sum, brand) => sum + (brand.realNumber || 0), 0)
        packaging.realNumber = totalReal

        // Set timestamps
        packaging.endTime = dateToTimestamp(new Date())

        const packagingIndex = packagings.indexOf(packaging)
        const nextPackaging = packagings[packagingIndex + 1]
        if (nextPackaging) nextPackaging.startTime = dateToTimestamp(new Date())
      })

      // Determine if last packaging(s)
      const isLast = Object.keys(updatesByPackaging).some(packagingId => {
        const packagingIndex = packagings.findIndex(packaging => packaging.packaging.id === packagingId)
				return packagingIndex === packagings.length - 1
      })

      if (isLast) await savePackagingExecutionNewStatus(packagingExecution, PE_STATUSES.waste)
      else await packagingExecution.save()

      dispatch({
        type: "PACKAGING_EXECUTION_LOADED",
        packagingExecution: packagingExecution.toJSON()
      })

      dispatch(openAppMessageSnackBar("Les nombres de barquettes ont bien été enregistrés"))
    } catch (error) {
      dispatch(openAppMessageSnackBar(error.message, "error"))
    }
  })
}

export const validatePackagingExecutionRealWaste = (packagingExecutionId, changedSections = []) => {
	return actionWithLoader(async (dispatch) => {
		try {
			if (!changedSections || !changedSections.length) {
				return
			}
			const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })

			const sections = packagingExecution.get("sections").map((section) => {
				const matchingChangedSection = changedSections.find((changedSection) => changedSection.section.objectId === section.section.id)
				if (matchingChangedSection) {
					section.realWaste = +matchingChangedSection.realWaste || 0
				}
				return section
			})

			packagingExecution.set("sections", sections)
			packagingExecution.set("endTime", dateToTimestamp(new Date()))

			await savePackagingExecutionNewStatus(packagingExecution, PE_STATUSES.done)

			dispatch({
				type: "PACKAGING_EXECUTION_LOADED",
				packagingExecution: packagingExecution.toJSON()
			})
			dispatch(openAppMessageSnackBar("Le waste a bien été sauvegardé"))
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

export const setPackagingExecutionGazTest = (packagingExecutionId, values) => {
	return actionWithLoader(async (dispatch, getState) => {
		try {
			const state = getState()
			const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })
			const user = getCurrentUser(state)
			const userPointer = new Parse.User()
			userPointer.id = user.objectId
			const gazTest = values.tests
			const updatedPackagingExecution = await packagingExecution.save({ gazTest })

			dispatch({
				type: "PACKAGING_EXECUTION_LOADED",
				packagingExecution: updatedPackagingExecution.toJSON()
			})

			dispatch(openAppMessageSnackBar("Le test gaz a bien été enregistré"))
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}


export const setPackagingExecutionToInProgress = (packagingExecutionId) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false, include: ["sections.section", "recipe"] })

			const startTime = dateToTimestamp(new Date())
			packagingExecution.set("startTime", startTime)

			const firstPackaging = packagingExecution.get("packagings")[0]

			if (firstPackaging) {
				firstPackaging.startTime = dateToTimestamp(new Date())
			}

			await savePackagingExecutionNewStatus(packagingExecution, PE_STATUSES.inProgress)
			dispatch({
				type: "PACKAGING_EXECUTION_LOADED",
				packagingExecution: packagingExecution.toJSON()
			})
			dispatch(openAppMessageSnackBar("Le barquettage est en cours"))
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

/**
 * this function is used to update or create a counterWeighing for a section
 * @NOTE to add a new counterWeighing, click the reset button in the counterWeighing form
 * so that will add the previous counterWeighing in the counterWeights array as a history
 */
export const updatedSectionsCounterWeight = (packagingExecutionId, values) => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const storedPackagingExecutions = getPackagingExecutionsSelector(state)
		const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })

		// 1. update db
		const packagingExecutionSections = packagingExecution.get("sections") || []
		const sectionsValues = values.sections || []
		const newSections = []

		for (const section of packagingExecutionSections) {
			const currentSectionValue = sectionsValues.find((sectionValue) => {
				const id = sectionValue.section.objectId || sectionValue.section.id
				return id === section.section.id
			})
			if (currentSectionValue?.counterWeighing) {
				const { weight, reason } = currentSectionValue.counterWeighing
				// the default value is the last element of the counterWeights array
				section.counterWeighing = {
					...section.counterWeighing,
					weight: +weight,
					reason: reason,
					date: dayjs().toDate(),
					user: Parse.User.current(),
				}
				section.productionWeight = section.counterWeighing.weight
			}

			newSections.push(section)
		}

		packagingExecution.set("sections", newSections)

		const events = (values.events || []).map((event) => {
			// if a json
			if (event.user.objectId) {
				const userPointer = new Parse.User()
				userPointer.id = event.user.objectId
				event.user = userPointer
			}

			return event
		})

		packagingExecution.set("events", events)

		const updatedPackagingExecution = await packagingExecution.save()

		// 2. update store
		const updatedPackagingExecutions = storedPackagingExecutions.map((pe) => {
			if (pe.objectId === updatedPackagingExecution.id) {
				return {
					...updatedPackagingExecution.toJSON(),
					sections: newSections,
					events: events
				}
			}
			return pe
		})

		dispatch({
			type: "PACKAGING_EXECUTIONS_LOADED",
			packagingExecutions: updatedPackagingExecutions
		})
		dispatch(openAppMessageSnackBar("La contre-pesée a bien été enregistrée"))
	})
}

export const updatePackagingExecutionToDoRealWeight = (packagingExecutionId, realNumber) => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const prevPackagingExecutions = getPackagingExecutionsSelector(state)
		const packagingExecution = await getPackagingExecutionById({ id: packagingExecutionId, toJson: false })
		const sections = packagingExecution.get("sections") || []

		// add realNumber to each section
		const newSections = sections.map((section) => {
			section.realNumber = realNumber
			return section
		})

		packagingExecution.set("sections", newSections)

		// update db
		const updatedPackagingExecution = await packagingExecution.save()

		// update store
		const packagingExecutions = prevPackagingExecutions.map((pe) => {
			if (pe.id === updatedPackagingExecution.id) {
				const updatedPackagingExecutionJson = updatedPackagingExecution.toJSON()
				return { ...pe, sections: updatedPackagingExecutionJson.sections }
			}
			return pe
		})

		dispatch({
			type: "PACKAGING_EXECUTIONS_LOADED",
			packagingExecutions: packagingExecutions
		})
	})
}

// ---------- REDIRECTION ---------- //
export const showPackagingExecutionFilter = () => {
	return push(PACKAGING_EXECUTION_URLS.filter)
}

export const showPackagingExecutionRead = (siteId, packagingDate) => {
	return push(`${PACKAGING_EXECUTION_URLS.read}/${siteId}/${packagingDate}`)
}

export const showPackagingExecutionsHoursEdit = (siteId, dateId) => {
	return push(`${PACKAGING_EXECUTION_URLS.hoursEdit}/${siteId}/${dateId}`)
}

export const showPackagingExecution = (siteId, date, peId) => {
	return push(`${PACKAGING_EXECUTION_URLS.pe}/${siteId}/${date}/${peId}`)
}

export const onEnterPackagingExecutionFilter = (store) => {
	return onEnter({
		store,
		actionThunk: loadPackagingExecutionsFilters,
		getReplacingPath: getReplacingPath({ needUser: true })
	})
}

export const onEnterPackagingExecutionRead = (store) => {
	return onEnter({
		store,
		actionThunk: (params) => async (dispatch) => {
			await dispatch(loadPackagingExecutionsRead(params))
			await dispatch(loadPlanningRules())
		},
		getReplacingPath: getReplacingPath({ needUser: true })
	})
}

export const onEnterPackagingExecutionHoursEdit = (store) => {
	return onEnter({
		store,
		actionThunk: loadPackagingExecutionsRead,
		getReplacingPath: getReplacingPath({ needUser: true })
	})
}

export const onEnterPackagingExecution = (store) => {
	return onEnter({
		store,
		actionThunk: loadPackagingExecution,
		getReplacingPath: getReplacingPath({ needUser: true })
	})
}

/**
 * Transitions the PackagingExecution from IN_PROGRESS to PACKAGINGS_COUNTING status.
 * - Fetches the full PackagingExecution from the server (including sections and recipe).
 * - Updates its status to "packagingsCounting".
 * - Dispatches the updated object to the Redux store.
 */
export const setPackagingExecutionToPackagingCounting = (packagingExecutionId, currentStep) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const packagingExecution = await getPackagingExecutionById({
				id: packagingExecutionId,
				toJson: false,
				include: ["sections.section", "recipe"]
			})

			await savePackagingExecutionNewStatus(packagingExecution, PE_STATUSES.packagingsCounting)

			dispatch({
				type: "PACKAGING_EXECUTION_LOADED",
				packagingExecution: packagingExecution.toJSON(),
			})

			if (packagingCardOrder.includes(currentStep)) {
				const packagingLabel = packagingNature.find(packaging => packaging.value === currentStep)?.label
				dispatch(openAppMessageSnackBar(`Barquettage ${packagingLabel.toLowerCase()} terminé`))
			}
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

/**
 * Updates packaging start and end timestamps.
 *
 * Steps:
 * - Fetches the PackagingExecution from the server with its packaging data.
 * - Applies the logic:
 *   → If status is "TODO": sets the startTime of the first active packaging.
 *   → If status is "IN_PROGRESS": sets the endTime of the current packaging and the startTime of the next one if applicable.
 *   → If nextStep is "packagingsCounting": sets only the endTime of the current packaging.
 * - Saves the updated PackagingExecution to the server.
 * - Dispatches the updated object to the Redux store.
 * - Displays a success or error message in the snackbar.
 */
export const setPackagingExecutionPackagingTimes = (packagingExecutionId, currentStep, nextStep, PEStatus) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const packagingExecution = await getPackagingExecutionById({
				id: packagingExecutionId,
				toJson: false,
				include: ["packagings"]
			})

			await savePackagingExecutionPackagingTimes(packagingExecution, currentStep, nextStep, PEStatus)

			dispatch({
				type: "PACKAGING_EXECUTION_LOADED",
				packagingExecution: packagingExecution.toJSON(),
			})

			if (packagingCardOrder.includes(currentStep)) {
				const packagingLabel = packagingNature.find(packaging => packaging.value === currentStep)?.label
				dispatch(openAppMessageSnackBar(`Barquettage ${packagingLabel.toLowerCase()} terminé`))
			}
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}